import { Deferred, Head, router, usePage } from "@inertiajs/react";
import type { ColumnDef, Table as TableTanstack } from "@tanstack/react-table";
import { useLocalStorage } from "@uidotdev/usehooks";
import { format, formatDistanceToNow } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
	CheckCircle,
	ClipboardListIcon,
	Copy,
	Ellipsis,
	Info,
	ListFilterIcon,
	Loader2,
	Mail,
	SearchIcon,
	Trash2,
	XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ChangeEmailDrawer from "@/components/admin-portal/patient/change-email";
import PaginationTable from "@/components/admin-portal/shared/data-table-pagination";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import UserDetailContent from "@/components/admin-portal/user/detail-content";
import { DeleteUserDialogContent } from "@/components/admin-portal/user/feature-actions";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCsrfToken } from "@/lib/csrf";
import { getRegistrationSourceLabel } from "@/lib/patients";
import {
	cn,
	copyToClipboard,
	debounce,
	populateQueryParams,
} from "@/lib/utils";
import type { UserDetailOrders, UserForAdmin } from "@/types/admin-portal/user";
import type { GlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

interface PageProps {
	users?: {
		data: UserForAdmin[];
		metadata: Metadata;
	};
	userDetails?: UserForAdmin;
	userOrders?: UserDetailOrders;
}

export type TableToolbarDataProps = TableTanstack<UserForAdmin>;
type TableColumnDef = ColumnDef<UserForAdmin>[];

const PREFERENCES_STORAGE_KEY = "admin-portal-user-preferences";

export default function Index({ users, userDetails, userOrders }: PageProps) {
	const { t } = useTranslation("users");
	const isMobile = useIsMobile();
	const { props: globalProps, url: pageURL } = usePage<GlobalPageProps>();
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);
	const [isDetailOpen, setIsDetailOpen] = useState(false);

	// ── Preferences ───────────────────────────────────────────────────────
	const [preferences, savePreferences] = useLocalStorage(
		PREFERENCES_STORAGE_KEY,
		{
			isShowFilter: false,
		},
	);

	const handleToggleFilter = useCallback(() => {
		savePreferences((prev) => ({ ...prev, isShowFilter: !prev.isShowFilter }));
	}, [savePreferences]);

	// ── Filters ────────────────────────────────────────────────────────────
	const [filters, setFilters] = useState(() => {
		const q = globalProps?.adminPortal?.currentQuery;
		return {
			search: q?.search || "",
			verified: q?.verified || "",
			role: q?.role || "",
			phoneExist: q?.phone_exist || "",
			orderExist: q?.order_exist || "",
			registrationSource: q?.registration_source || "",
		};
	});

	const [isSearching, setIsSearching] = useState(false);

	const fetchUserDetails = useCallback(
		async (userId: string, orderPage = 1, orderLimit = 5) => {
			setIsDetailOpen(true);
			setIsLoadingDetail(true);
			router.get(
				pageURL,
				{
					...(globalProps.adminPortal.currentQuery || {}),
					user_id: userId,
					order_page: orderPage,
					order_limit: orderLimit,
				},
				{
					preserveState: true,
					preserveScroll: true,
					only: ["userDetails", "userOrders"],
					onStart: () => setIsLoadingDetail(true),
					onFinish: () => setIsLoadingDetail(false),
				},
			);
		},
		[pageURL, globalProps.adminPortal.currentQuery],
	);

	const handleUserOrdersPageChange = useCallback(
		(page: number, limit?: number) => {
			if (userDetails?.id) {
				fetchUserDetails(userDetails.id, page, limit);
			}
		},
		[userDetails?.id, fetchUserDetails],
	);

	const debouncedUpdate = useCallback(
		debounce((url: string) => {
			router.get(
				url,
				{},
				{
					preserveState: true,
					replace: true,
					only: ["users", "adminPortal", "flash"],
					onStart: () => setIsSearching(true),
					onFinish: () => setIsSearching(false),
				},
			);
		}, 500),
		[],
	);

	const handleFilterChange = useCallback(
		(key: keyof typeof filters, value: string) => {
			const nextFilters = { ...filters, [key]: value };
			setFilters(nextFilters);

			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...nextFilters,
					page: null,
				}),
			);
			debouncedUpdate(fullUrl);
		},
		[filters, pageURL, debouncedUpdate],
	);

	const handleResetAllFilters = useCallback(() => {
		const resetState = {
			search: "",
			verified: "",
			role: "",
			phoneExist: "",
			orderExist: "",
			registrationSource: "",
		};
		setFilters(resetState);

		const { fullUrl } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({
				...resetState,
				page: null,
			}),
		);
		router.get(
			fullUrl,
			{},
			{
				preserveState: true,
				replace: true,
				only: ["users", "adminPortal", "flash"],
			},
		);
	}, [pageURL]);

	const activeFilters = useMemo(() => {
		const result: {
			key: keyof typeof filters;
			label: string;
			value: string;
		}[] = [];
		if (filters.search) {
			result.push({
				key: "search",
				label: t("filter.search.label"),
				value: filters.search,
			});
		}
		if (filters.verified) {
			result.push({
				key: "verified",
				label: t("filter.verified.label"),
				value:
					filters.verified === "true"
						? t("filter.verified.items.verified")
						: t("filter.verified.items.unverified"),
			});
		}
		if (filters.role) {
			result.push({
				key: "role",
				label: t("filter.role.label"),
				value: t(`filter.role.items.${filters.role.toLowerCase()}`),
			});
		}
		if (filters.phoneExist) {
			result.push({
				key: "phoneExist",
				label: t("filter.phone_exist.label"),
				value:
					filters.phoneExist === "true"
						? t("filter.phone_exist.items.exist")
						: t("filter.phone_exist.items.not_exist"),
			});
		}
		if (filters.orderExist) {
			result.push({
				key: "orderExist",
				label: t("filter.order_exist.label"),
				value:
					filters.orderExist === "true"
						? t("filter.order_exist.items.exist")
						: t("filter.order_exist.items.not_exist"),
			});
		}
		if (filters.registrationSource) {
			result.push({
				key: "registrationSource",
				label: t("filter.registration_source.label"),
				value: filters.registrationSource.replace("_", " "),
			});
		}
		return result;
	}, [filters, t]);

	const [isVerifying, setIsVerifying] = useState(false);
	const [userIdToVerify, setUserIdToVerify] = useState<string | null>(null);
	const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null);
	const [changeEmailTarget, setChangeEmailTarget] = useState<{
		userId: string;
		name: string;
		currentEmail: string | null | undefined;
	} | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: -
	useEffect(() => {
		const searchParams = new URLSearchParams(
			new URL(pageURL, window.location.origin).search,
		);
		const userId = searchParams.get("user_id");

		if (userId) {
			if (!isDetailOpen || (userDetails && userDetails.id !== userId)) {
				fetchUserDetails(userId);
			}
		} else if (isDetailOpen) {
			setIsDetailOpen(false);
		}
	}, [pageURL]);

	const onVerify = useCallback(
		async (userId?: string) => {
			if (!userId) {
				toast.error("User ID is missing");
				return false;
			}

			setIsVerifying(true);
			try {
				const csrfToken = getCsrfToken();

				const response = await fetch(
					`/admin-portal/user-management/${userId}/verify`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
							"X-CSRF-Token": csrfToken,
						},
					},
				);

				const data = await response.json();

				if (response.ok && data.success) {
					toast.success(data.message || t("messages.verify_success"));
					router.reload({ only: ["adminPortal", "flash", "errors", "users"] });
					return true;
				}
				toast.error(data.message || t("messages.verify_error"));
				return false;
			} catch (_error) {
				toast.error(t("messages.verify_error"));
				return false;
			} finally {
				setIsVerifying(false);
			}
		},
		[t],
	);

	const columns = useMemo<TableColumnDef>(() => {
		const items: TableColumnDef = [
			{
				accessorKey: "email",
				enableSorting: true,
				enableHiding: false,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t("details.email")} />
				),
				cell: ({ row }) => {
					return (
						<div className="flex items-center gap-1 group/item max-w-[200px]">
							<p className="font-semibold truncate">{row.original.email}</p>
							<Button
								variant="ghost"
								size="icon"
								className="h-4 w-4 opacity-0 group-hover/item:opacity-100 transition-opacity"
								onClick={() => {
									copyToClipboard(row.original.email);
									toast.success(
										t("messages.copied_to_clipboard", "Copied to clipboard"),
									);
								}}
							>
								<Copy className="h-3 w-3" />
							</Button>
						</div>
					);
				},
			},
			{
				accessorKey: "phoneNumber",
				enableSorting: true,
				enableHiding: false,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t("details.phone")} />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-1 group/item max-w-[150px]">
						<span className="text-sm font-mono tracking-tighter text-muted-foreground/75 truncate">
							{row.original.phoneNumber || "N/A"}
						</span>
						{row.original.phoneNumber && (
							<Button
								variant="ghost"
								size="icon"
								className="h-4 w-4 opacity-0 group-hover/item:opacity-100 transition-opacity"
								onClick={() => {
									copyToClipboard(row.original.phoneNumber as string);
									toast.success(
										t("messages.copied_to_clipboard", "Copied to clipboard"),
									);
								}}
							>
								<Copy className="h-3 w-3" />
							</Button>
						)}
					</div>
				),
			},
		];

		if (!isMobile) {
			items.push(
				{
					accessorKey: "roles",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader column={column} title={t("list.roles")} />
					),
					cell: ({ row }) => (
						<div className="flex flex-wrap gap-1">
							{row.original.roles.map((role: string) => (
								<Badge
									key={role}
									variant="accent"
									className="px-1.5 py-1 text-[10px] uppercase rounded-full"
								>
									{role}
								</Badge>
							))}
						</div>
					),
				},
				{
					accessorKey: "isVerified",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.verified.label")}
						/>
					),
					cell: ({ row }) => {
						const isVerified = row.original.isVerified;
						return (
							<div>
								<Badge
									variant="accent"
									className={cn(
										"px-1.5 py-1 rounded-full",
										isVerified ? "bg-green-100" : "bg-red-100",
									)}
								>
									<span
										className={cn(
											"flex items-center gap-1 text-[10px] uppercase",
											isVerified ? "text-emerald-500" : "text-red-500",
										)}
									>
										{isVerified
											? t("list.verified.verified")
											: t("list.verified.unverified")}
									</span>
								</Badge>
							</div>
						);
					},
				},
				{
					accessorKey: "registrationSource",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.registration_source")}
						/>
					),
					cell: ({ row }) => (
						<span className="text-xs italic uppercase text-muted-foreground/75">
							{getRegistrationSourceLabel(row?.original?.registrationSource)}
						</span>
					),
				},
				{
					id: "activity",
					header: ({ column }) => (
						<DataTableColumnHeader column={column} title={t("list.activity")} />
					),
					cell: ({ row }) => {
						const createdAt = new Date(row.original.createdAt);
						const lastSignInAt = row.original.lastSignInAt
							? new Date(row.original.lastSignInAt)
							: null;

						return (
							<div className="flex flex-col">
								<span className="text-sm text-muted-foreground tracking-tight">
									{format(createdAt, "dd MMM yyyy")}
								</span>
								<span className="text-xs text-muted-foreground/75 tracking-tight italic">
									{lastSignInAt
										? t("list.last_active", {
												time: formatDistanceToNow(lastSignInAt, {
													addSuffix: true,
												}),
												defaultValue: `Last active ${formatDistanceToNow(
													lastSignInAt,
													{ addSuffix: true },
												)}`,
											})
										: t("list.never_active", "Never active")}
								</span>
							</div>
						);
					},
				},

				{
					accessorKey: "orderCount",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.orders_count")}
						/>
					),
					cell: ({ row }) => (
						<Badge variant="accent" className="px-1 rounded-full">
							<p className="flex items-center gap-1 font-medium">
								<ClipboardListIcon className="size-3 shrink-0" />
								<span className="text-nowrap">
									{row.original.orderCount || 0} Orders
								</span>
							</p>
						</Badge>
					),
				},
				{
					accessorKey: "isSuspended",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.status.label")}
						/>
					),
					cell: ({ row }) => {
						const isSuspended = row.original.isSuspended;
						return (
							<div>
								<Badge
									variant="accent"
									className={cn(
										"px-1.5 py-1 rounded-full",
										isSuspended ? "bg-red-100" : "bg-green-100",
									)}
								>
									<span
										className={cn(
											"text-[10px] uppercase font-bold",
											isSuspended ? "text-red-500" : "text-emerald-500",
										)}
									>
										{isSuspended
											? t("list.status.suspended")
											: t("list.status.active")}
									</span>
								</Badge>
							</div>
						);
					},
				},
			);
		}

		items.push({
			id: "actions",
			enableSorting: false,
			enableHiding: false,
			cell: ({ row }) => {
				const isUnverified = !row.original.isVerified;
				const hasNoOrders = row.original.orderCount === 0;

				return (
					<div className="flex items-center justify-end">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="icon">
									<Ellipsis />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuLabel>{t("list.actions")}</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuGroup>
									<DropdownMenuItem
										onSelect={() => fetchUserDetails(row.original.id)}
									>
										<Info className="shrink-0 text-muted-foreground/75" />
										{t("button.detail")}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										disabled={isVerifying || row.original.isVerified}
										onSelect={() => setUserIdToVerify(row.original.id)}
									>
										<CheckCircle className="shrink-0 text-muted-foreground/75" />
										{t("button.verify")}
									</DropdownMenuItem>
									<DropdownMenuItem
										onSelect={() =>
											setChangeEmailTarget({
												userId: row.original.id,
												name:
													[row.original.firstName, row.original.lastName]
														.filter(Boolean)
														.join(" ") || row.original.email,
												currentEmail: row.original.email,
											})
										}
									>
										<Mail className="shrink-0 text-muted-foreground/75" />
										{t("button.change_email")}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										disabled={!isUnverified || !hasNoOrders}
										onSelect={() => setUserIdToDelete(row.original.id)}
									>
										<Trash2 className="shrink-0 text-destructive/75" />
										<span className="text-destructive">
											{t("button.delete")}
										</span>
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				);
			},
		});

		return items;
	}, [isMobile, t, isVerifying, fetchUserDetails]);

	return (
		<>
			<Head title={t("head_title")} />

			<PageContainer className="min-h-[100vh] grid grid-cols-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("page_title")}
						</h1>
						<p className="w-full text-sm md:w-8/12 text-pretty text-muted-foreground">
							{t("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<div className="z-10 flex items-center justify-between gap-2">
					<div>
						<Button
							type="button"
							variant={preferences.isShowFilter ? "default" : "outline"}
							size={isMobile ? "icon" : "default"}
							className="shadow-none"
							onClick={(event) => {
								event.preventDefault();
								handleToggleFilter();
							}}
						>
							<ListFilterIcon className="size-4" />
							{isMobile
								? null
								: preferences.isShowFilter
									? t("filter.close")
									: t("filter.label")}
						</Button>
					</div>
				</div>

				<AnimatePresence initial={false}>
					{preferences.isShowFilter && (
						<motion.section
							initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							transition={{ duration: 0.3, ease: "easeOut" }}
							className="p-4 border shadow-inner rounded-xl bg-background"
						>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
								<div className="col-span-full xl:col-span-2">
									<Input
										value={filters.search}
										placeholder={t("filter.search.placeholder")}
										className="shadow-inner bg-input"
										StartIcon={{ icon: SearchIcon }}
										isLoading={isSearching}
										EndIcon={
											filters.search
												? {
														icon: XIcon,
														isButton: true,
														handleOnClick: () =>
															handleFilterChange("search", ""),
													}
												: undefined
										}
										onChange={(e) =>
											handleFilterChange("search", e.target.value)
										}
									/>
								</div>

								<Select
									value={filters.verified}
									onValueChange={(v) => handleFilterChange("verified", v)}
								>
									<SelectTrigger className="shadow-inner bg-input">
										<SelectValue
											placeholder={t("filter.verified.placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="true">
											{t("filter.verified.items.verified").toUpperCase()}
										</SelectItem>
										<SelectItem value="false">
											{t("filter.verified.items.unverified").toUpperCase()}
										</SelectItem>
									</SelectContent>
								</Select>

								<Select
									value={filters.role}
									onValueChange={(v) => handleFilterChange("role", v)}
								>
									<SelectTrigger className="shadow-inner bg-input">
										<SelectValue placeholder={t("filter.role.placeholder")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="USER">
											{t("filter.role.items.user").toUpperCase()}
										</SelectItem>
										<SelectItem value="ADMIN">
											{t("filter.role.items.admin").toUpperCase()}
										</SelectItem>
										<SelectItem value="THERAPIST">
											{t("filter.role.items.therapist").toUpperCase()}
										</SelectItem>
									</SelectContent>
								</Select>

								<Select
									value={filters.phoneExist}
									onValueChange={(v) => handleFilterChange("phoneExist", v)}
								>
									<SelectTrigger className="shadow-inner bg-input">
										<SelectValue
											placeholder={t("filter.phone_exist.placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="true">
											{t("filter.phone_exist.items.exist").toUpperCase()}
										</SelectItem>
										<SelectItem value="false">
											{t("filter.phone_exist.items.not_exist").toUpperCase()}
										</SelectItem>
									</SelectContent>
								</Select>

								<Select
									value={filters.orderExist}
									onValueChange={(v) => handleFilterChange("orderExist", v)}
								>
									<SelectTrigger className="shadow-inner bg-input">
										<SelectValue
											placeholder={t("filter.order_exist.placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="true">
											{t("filter.order_exist.items.exist").toUpperCase()}
										</SelectItem>
										<SelectItem value="false">
											{t("filter.order_exist.items.not_exist").toUpperCase()}
										</SelectItem>
									</SelectContent>
								</Select>

								<Select
									value={filters.registrationSource}
									onValueChange={(v) =>
										handleFilterChange("registrationSource", v)
									}
								>
									<SelectTrigger className="shadow-inner bg-input">
										<SelectValue
											placeholder={t("filter.registration_source.placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										{["WEB", "MOBILE_ANDROID", "MOBILE_IOS", "ADMIN_PANEL"].map(
											(source) => (
												<SelectItem key={source} value={source}>
													{source.replaceAll("_", " ")}
												</SelectItem>
											),
										)}
									</SelectContent>
								</Select>
							</div>

							{activeFilters.length > 0 && (
								<div className="flex flex-wrap items-center gap-2 mt-6">
									<span className="text-xs font-medium text-muted-foreground">
										{t("filter.active_filters")}:
									</span>
									{activeFilters.map((f) => (
										<Badge
											key={f.key}
											variant="secondary"
											className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[10px] uppercase tracking-wide"
										>
											<span>{f.label}:</span>
											<span className="font-bold">{f.value}</span>
											<button
												type="button"
												onClick={() => handleFilterChange(f.key, "")}
												className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
											>
												<XIcon className="h-3 w-3" />
											</button>
										</Badge>
									))}

									<Button
										variant="destructive-outline"
										size="sm"
										className="rounded-full"
										onClick={handleResetAllFilters}
									>
										{t("filter.reset_all")}
									</Button>
								</div>
							)}

							<div className="flex flex-col justify-end gap-2 mt-4 md:flex-row">
								<Button
									variant="ghost"
									size="sm"
									onClick={handleToggleFilter}
									className="w-full md:w-fit"
								>
									{t("filter.close")}
								</Button>
								<Button
									variant="ghost-destructive"
									size="sm"
									onClick={handleResetAllFilters}
									className="w-full md:w-fit"
								>
									{t("filter.reset_all")}
								</Button>
							</div>
						</motion.section>
					)}
				</AnimatePresence>

				<Deferred
					data="users"
					fallback={
						<div className="flex flex-col items-center justify-center h-64 gap-4 border border-dashed rounded-lg bg-muted/50">
							<Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								{t("list.loading_users", "Loading users...")}
							</p>
						</div>
					}
				>
					<DataTable
						columns={columns}
						data={users?.data || []}
						customPagination={(table) => (
							<PaginationTable
								table={table}
								metadata={users?.metadata || ({} as Metadata)}
							/>
						)}
					/>
				</Deferred>

				<AlertDialog
					open={!!userIdToVerify}
					onOpenChange={(open) => !open && setUserIdToVerify(null)}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t("confirm.verify_title")}</AlertDialogTitle>
							<AlertDialogDescription>
								{t("confirm.verify")}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isVerifying}>
								{t("button.cancel")}
							</AlertDialogCancel>
							<Button
								disabled={isVerifying}
								onClick={async () => {
									if (userIdToVerify) {
										const success = await onVerify(userIdToVerify);
										if (success) {
											setUserIdToVerify(null);
										}
									}
								}}
							>
								{isVerifying && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								{t("button.verify")}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				{changeEmailTarget && (
					<ChangeEmailDrawer
						open={!!changeEmailTarget}
						onOpenChange={(open) => !open && setChangeEmailTarget(null)}
						userId={changeEmailTarget.userId}
						patientName={changeEmailTarget.name}
						currentEmail={changeEmailTarget.currentEmail}
					/>
				)}

				<DeleteUserDialogContent
					user={
						userIdToDelete
							? users?.data?.find((u) => u.id === userIdToDelete) || null
							: null
					}
					open={!!userIdToDelete}
					onOpenChange={(open) => !open && setUserIdToDelete(null)}
				/>

				<UserDetailContent
					open={isDetailOpen}
					onOpenChange={(v) => {
						setIsDetailOpen(v);
						if (!v) {
							const { fullUrl } = populateQueryParams(pageURL, {
								user_id: null,
								order_page: null,
								order_limit: null,
							});
							router.visit(fullUrl, {
								method: "get",
								replace: true,
								preserveState: true,
								preserveScroll: true,
								only: [
									"adminPortal",
									"flash",
									"errors",
									"userDetails",
									"userOrders",
								],
							});
						}
					}}
					user={userDetails || null}
					orders={userOrders || null}
					isLoading={isLoadingDetail}
					onPageChange={handleUserOrdersPageChange}
				/>
			</PageContainer>
		</>
	);
}
